import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';

// ─── Pre-computed spiral dots ─────────────────────────────────────────────────

function buildSpiralDots(armColors, step, clusterBase) {
  const dots = [];
  for (let arm = 0; arm < 2; arm++) {
    const armOffset = arm * Math.PI;
    for (let t = 0; t < 4.4; t += step) {
      const rNorm = 0.07 + 0.2 * t;
      const prog  = t / 4.4;
      const n     = clusterBase + Math.round((1 - prog) * clusterBase);
      const color = prog < 0.33 ? armColors[0] : prog < 0.66 ? armColors[1] : armColors[2];
      for (let c = 0; c < n; c++) {
        dots.push({
          r:       rNorm + (Math.random() - 0.5) * rNorm * 0.18,
          angle:   t + armOffset + (Math.random() - 0.5) * 0.1,
          size:    (1 - prog * 0.55) * 1.8 + 0.3,
          opacity: (1 - prog * 0.65) * 0.26 + 0.04,
          color,
        });
      }
    }
  }
  return dots;
}

function buildStars(palette, count) {
  return Array.from({ length: count }, () => ({
    angle:      Math.random() * Math.PI * 2,
    distFactor: Math.pow(Math.random(), 0.65),
    r:          Math.random() * 1.8 + 0.3,
    maxOpacity: 0.15 + Math.random() * 0.55,
    color:      palette[Math.floor(Math.random() * palette.length)],
  }));
}

// Main galaxy  – cyan → violet → magenta
const MAIN_SPIRAL = buildSpiralDots([[0,217,255],[124,60,255],[240,43,255]], 0.05, 3);
const MAIN_STARS  = buildStars([[0,217,255],[124,60,255],[255,255,255],[240,43,255],[24,119,255]], 840);

// Second galaxy – magenta → blue → cyan
const SEC_SPIRAL  = buildSpiralDots([[240,43,255],[24,119,255],[0,217,255]], 0.07, 2);
const SEC_STARS   = buildStars([[240,43,255],[24,119,255],[255,255,255],[0,217,255]], 330);

// Shooting star color palette
const METEOR_COLORS = [
  [255, 255, 255],
  [255, 255, 255],
  [210, 235, 255],
  [210, 235, 255],
  [0,   217, 255],
  [240,  43, 255],
];

// ─── Galaxy drawing helper ────────────────────────────────────────────────────

function drawGalaxy(ctx, { cx, cy, R, discR, rotation, coreGlow, tilt,
                            halo, disc, coreStops, spiral, stars, starOp }) {
  const haloG = ctx.createRadialGradient(cx, cy, 0, cx, cy, R);
  halo.forEach(s => haloG.addColorStop(s[0], `rgba(${s[1]},${s[2]},${s[3]},${s[4]})`));
  ctx.fillStyle = haloG;
  ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.fill();

  ctx.save();
  ctx.translate(cx, cy); ctx.rotate(rotation + tilt);
  const discG = ctx.createRadialGradient(0, 0, 0, 0, 0, discR);
  disc.forEach(s => discG.addColorStop(s[0], `rgba(${s[1]},${s[2]},${s[3]},${s[4]})`));
  ctx.scale(1, 0.28); ctx.fillStyle = discG;
  ctx.beginPath(); ctx.arc(0, 0, discR, 0, Math.PI * 2); ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.translate(cx, cy); ctx.rotate(rotation + tilt);
  spiral.forEach(dot => {
    const pr = dot.r * discR;
    const x  = Math.cos(dot.angle) * pr;
    const y  = Math.sin(dot.angle) * pr * 0.28;
    ctx.beginPath(); ctx.arc(x, y, dot.size, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${dot.color.join(',')},${dot.opacity})`;
    ctx.fill();
  });
  ctx.restore();

  const coreR = discR * 0.12;
  const coreG = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR);
  coreStops.forEach(s =>
    coreG.addColorStop(s[0], `rgba(${s[1]},${s[2]},${s[3]},${(s[4] * coreGlow).toFixed(3)})`)
  );
  ctx.fillStyle = coreG;
  ctx.beginPath(); ctx.arc(cx, cy, coreR, 0, Math.PI * 2); ctx.fill();

  stars.forEach((star, i) => {
    const op = starOp[i].v;
    if (op <= 0.005) return;
    const dist  = star.distFactor * R * 0.94;
    const angle = star.angle + rotation * 0.12;
    ctx.beginPath();
    ctx.arc(cx + Math.cos(angle) * dist, cy + Math.sin(angle) * dist * 0.42, star.r, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${star.color.join(',')},${op})`;
    ctx.fill();
  });
}

// ─── Shooting star drawing helper ────────────────────────────────────────────

function drawMeteors(ctx, meteors) {
  meteors.forEach(m => {
    if (m.opacity <= 0.005) return;

    const traveled  = m.offset * m.travelDist;
    const headX     = m.startX + Math.cos(m.angle) * traveled;
    const headY     = m.startY + Math.sin(m.angle) * traveled;
    const tailDist  = Math.max(0, traveled - m.trailLen);
    const tailX     = m.startX + Math.cos(m.angle) * tailDist;
    const tailY     = m.startY + Math.sin(m.angle) * tailDist;

    const grad = ctx.createLinearGradient(tailX, tailY, headX, headY);
    grad.addColorStop(0,   `rgba(${m.color},0)`);
    grad.addColorStop(0.5, `rgba(${m.color},${(m.opacity * 0.35).toFixed(3)})`);
    grad.addColorStop(1,   `rgba(${m.color},${m.opacity.toFixed(3)})`);

    ctx.beginPath();
    ctx.moveTo(tailX, tailY);
    ctx.lineTo(headX, headY);
    ctx.strokeStyle = grad;
    ctx.lineWidth   = 1.5;
    ctx.lineCap     = 'round';
    ctx.stroke();

    // Bright head dot
    ctx.beginPath();
    ctx.arc(headX, headY, 1.8, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${m.color},${m.opacity.toFixed(3)})`;
    ctx.fill();
  });
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function GalaxyBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width = 0, height = 0;

    const onResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    onResize();
    window.addEventListener('resize', onResize);

    const state  = { rot1: 0, rot2: 0, core1: 0.55, core2: 0.50 };
    const mainOp = MAIN_STARS.map(() => ({ v: 0 }));
    const secOp  = SEC_STARS.map(() => ({ v: 0 }));

    // ── Shooting stars ────────────────────────────────────────────────
    const meteors = Array.from({ length: 6 }, () => ({
      startX: 0, startY: 0, angle: 0,
      trailLen: 0, travelDist: 0,
      offset: 0, opacity: 0,
      color: '255,255,255',
    }));

    const delayedCalls = [];

    function fireMeteor(m) {
      const c = METEOR_COLORS[Math.floor(Math.random() * METEOR_COLORS.length)];
      m.color      = c.join(',');
      m.startX     = Math.random() * width;
      m.startY     = Math.random() * height * 0.65;
      m.angle      = Math.PI / 6 + Math.random() * (Math.PI / 4); // 30°–75° downward
      m.trailLen   = 90 + Math.random() * 160;
      m.travelDist = m.trailLen + 80 + Math.random() * 220;
      m.offset     = 0;
      m.opacity    = 0;

      const dur = 0.45 + Math.random() * 0.85;

      gsap.timeline({ onComplete: () => scheduleMeteor(m) })
        .to(m, { opacity: 0.92, duration: 0.07, ease: 'power3.out' })
        .to(m, { offset: 1,     duration: dur,  ease: 'sine.in'    }, '<')
        .to(m, { opacity: 0,    duration: dur * 0.4, ease: 'power2.in' }, `<+=${dur * 0.6}`);
    }

    function scheduleMeteor(m) {
      const delay = 6 + Math.random() * 18;
      const dc = gsap.delayedCall(delay, () => fireMeteor(m));
      delayedCalls.push(dc);
    }

    // Stagger initial spawns so they don't all appear at once
    meteors.forEach((m, i) => {
      const dc = gsap.delayedCall(3 + i * 2.5 + Math.random() * 4, () => fireMeteor(m));
      delayedCalls.push(dc);
    });

    // ── Draw loop ─────────────────────────────────────────────────────
    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      const sc = Math.min(width, height);

      const R1 = sc * 3.24;
      drawGalaxy(ctx, {
        cx: width * 0.68, cy: height * 0.66,
        R: R1, discR: R1 * 0.58,
        rotation: state.rot1, coreGlow: state.core1, tilt: 0.52,
        halo: [
          [0,    124, 60, 255, 0.16], [0.28,  24, 119, 255, 0.10],
          [0.58,   0, 217, 255, 0.05], [1,     0,   0,   0, 0  ],
        ],
        disc: [
          [0,    230, 248, 255, 0.34], [0.10,   0, 217, 255, 0.28],
          [0.28, 124,  60, 255, 0.19], [0.52, 240,  43, 255, 0.11],
          [0.78,  24, 119, 255, 0.05], [1,     0,   0,   0, 0  ],
        ],
        coreStops: [
          [0,    255, 255, 255, 1.00], [0.14, 200, 248, 255, 0.78],
          [0.32,   0, 217, 255, 0.50], [0.60, 124,  60, 255, 0.22],
          [1,      0,   0,   0, 0  ],
        ],
        spiral: MAIN_SPIRAL, stars: MAIN_STARS, starOp: mainOp,
      });

      const R2 = sc * 1.36;
      drawGalaxy(ctx, {
        cx: width * 0.09, cy: height * 0.15,
        R: R2, discR: R2 * 0.58,
        rotation: state.rot2, coreGlow: state.core2, tilt: -0.8,
        halo: [
          [0,    240,  43, 255, 0.15], [0.30,  24, 119, 255, 0.10],
          [0.62, 124,  60, 255, 0.05], [1,      0,   0,   0, 0  ],
        ],
        disc: [
          [0,    255, 220, 255, 0.32], [0.12, 240,  43, 255, 0.26],
          [0.32,  24, 119, 255, 0.17], [0.56,   0, 217, 255, 0.09],
          [0.80, 124,  60, 255, 0.04], [1,      0,   0,   0, 0  ],
        ],
        coreStops: [
          [0,    255, 255, 255, 1.00], [0.15, 255, 200, 255, 0.78],
          [0.34, 240,  43, 255, 0.50], [0.62,  24, 119, 255, 0.22],
          [1,      0,   0,   0, 0  ],
        ],
        spiral: SEC_SPIRAL, stars: SEC_STARS, starOp: secOp,
      });

      drawMeteors(ctx, meteors);
    };

    gsap.ticker.add(draw);
    gsap.to(canvas, { opacity: 1, duration: 2.5, ease: 'power2.inOut' });
    gsap.to(state, { rot1:  Math.PI * 2, duration: 400, repeat: -1, ease: 'none' });
    gsap.to(state, { rot2: -Math.PI * 2, duration: 400, repeat: -1, ease: 'none' });
    gsap.to(state, { core1: 0.80, duration: 2.8, repeat: -1, yoyo: true, ease: 'sine.inOut' });
    gsap.to(state, { core2: 0.72, duration: 2.2, repeat: -1, yoyo: true, ease: 'sine.inOut', delay: 1.1 });

    mainOp.forEach((op, i) => gsap.to(op, {
      v: MAIN_STARS[i].maxOpacity, duration: 2 + Math.random() * 5,
      repeat: -1, yoyo: true, ease: 'sine.inOut', delay: Math.random() * 7,
    }));
    secOp.forEach((op, i) => gsap.to(op, {
      v: SEC_STARS[i].maxOpacity, duration: 2 + Math.random() * 4,
      repeat: -1, yoyo: true, ease: 'sine.inOut', delay: Math.random() * 5,
    }));

    return () => {
      gsap.ticker.remove(draw);
      window.removeEventListener('resize', onResize);
      gsap.killTweensOf(canvas);
      gsap.killTweensOf(state);
      mainOp.forEach(op => gsap.killTweensOf(op));
      secOp.forEach(op => gsap.killTweensOf(op));
      meteors.forEach(m => gsap.killTweensOf(m));
      delayedCalls.forEach(dc => dc.kill());
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed', top: 0, left: 0,
        width: '100%', height: '100%',
        pointerEvents: 'none', zIndex: -1, opacity: 0,
      }}
      aria-hidden="true"
    />
  );
}
